import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Toast from '../../components/Toast';
import { getPlatformWallet } from '../../services/HydraService';
import './PlatformWallet.css';

const COLORS = [
    '#21e6c1',
    '#33b8ff',
    '#7c5cff',
    '#ffd166',
    '#ff5f8f',
    '#5bf0dc',
    '#8ea7bd',
    '#ff8a4d',
    '#65a30d',
    '#db2777'
];

const TOP_SLICES = 10;

function formatUsd(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0,00';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

function formatQty(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';
    return number.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 8
    });
}

function formatShare(share) {
    const percent = Number(share) * 100;
    if (!Number.isFinite(percent) || percent <= 0) return '0%';
    if (percent < 0.1) return '<0,1%';
    return `${percent.toLocaleString('pt-BR', {
        minimumFractionDigits: percent < 10 ? 1 : 0,
        maximumFractionDigits: percent < 10 ? 1 : 0
    })}%`;
}

function apiError(err) {
    const raw = err?.response ? err.response.data : err?.message;
    if (typeof raw === 'string') return raw;
    if (raw && raw.message) return raw.message;
    return raw ? JSON.stringify(raw) : 'Não foi possível carregar a carteira da plataforma.';
}

function polar(cx, cy, radius, angleDeg) {
    const rad = (angleDeg - 90) * Math.PI / 180;
    return {
        x: cx + radius * Math.cos(rad),
        y: cy + radius * Math.sin(rad)
    };
}

function donutPath(cx, cy, outer, inner, start, end) {
    const sweep = end - start;
    if (sweep >= 359.999) {
        return [
            `M ${cx} ${cy - outer}`,
            `A ${outer} ${outer} 0 1 1 ${cx} ${cy + outer}`,
            `A ${outer} ${outer} 0 1 1 ${cx} ${cy - outer}`,
            `M ${cx} ${cy - inner}`,
            `A ${inner} ${inner} 0 1 0 ${cx} ${cy + inner}`,
            `A ${inner} ${inner} 0 1 0 ${cx} ${cy - inner}`,
            'Z'
        ].join(' ');
    }
    const large = sweep > 180 ? 1 : 0;
    const p1 = polar(cx, cy, outer, start);
    const p2 = polar(cx, cy, outer, end);
    const p3 = polar(cx, cy, inner, end);
    const p4 = polar(cx, cy, inner, start);
    return [
        `M ${p1.x} ${p1.y}`,
        `A ${outer} ${outer} 0 ${large} 1 ${p2.x} ${p2.y}`,
        `L ${p3.x} ${p3.y}`,
        `A ${inner} ${inner} 0 ${large} 0 ${p4.x} ${p4.y}`,
        'Z'
    ].join(' ');
}

function withColors(wallet) {
    return (wallet || [])
        .map(row => ({
            ...row,
            asset: String(row.asset || '').toUpperCase(),
            available: Number(row.available) || 0,
            onOrder: Number(row.onOrder) || 0,
            fiatEstimate: Number(row.fiatEstimate) || 0
        }))
        .filter(row => row.available > 0 || row.onOrder > 0)
        .sort((a, b) => (b.fiatEstimate - a.fiatEstimate) || a.asset.localeCompare(b.asset))
        .map((row, index) => ({
            ...row,
            color: COLORS[index % COLORS.length]
        }));
}

function slicesFromCoins(coins, totalHint) {
    const valued = coins.filter(row => row.fiatEstimate > 0);
    const total = valued.reduce((sum, row) => sum + row.fiatEstimate, 0) || Number(totalHint) || 0;
    if (!(total > 0) || !valued.length)
        return { slices: [], total };

    let list = valued;
    if (valued.length > TOP_SLICES) {
        const head = valued.slice(0, TOP_SLICES);
        const restValue = valued.slice(TOP_SLICES).reduce((sum, row) => sum + row.fiatEstimate, 0);
        if (restValue > 0) {
            head.push({
                asset: 'OUTRAS',
                fiatEstimate: restValue,
                color: '#64748b',
                otherCount: valued.length - TOP_SLICES
            });
        }
        list = head;
    }

    let cursor = 0;
    const gap = list.length > 1 ? 1.1 : 0;
    return {
        total,
        slices: list.map(row => {
            const share = row.fiatEstimate / total;
            const sweep = Math.max(share * 360 - gap, 0.35);
            const start = cursor;
            const end = Math.min(360, start + sweep);
            cursor = end + gap;
            return { ...row, share, start, end };
        })
    };
}

function PlatformWallet() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [active, setActive] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });

    function loadWallet() {
        setLoading(true);
        getPlatformWallet()
            .then(result => {
                setData(result);
                setLoading(false);
            })
            .catch(err => {
                setNotification({ type: 'error', text: apiError(err) });
                setLoading(false);
            });
    }

    useEffect(() => {
        loadWallet();
    }, []);

    const coins = useMemo(() => withColors(data?.wallet), [data]);
    const chart = useMemo(() => slicesFromCoins(coins, data?.walletTotal), [coins, data]);
    const lockedCount = coins.filter(row => row.onOrder > 0).length;
    const query = search.trim().toUpperCase();
    const visible = coins.filter(row => {
        if (filter === 'locked' && !(row.onOrder > 0)) return false;
        if (query && !row.asset.includes(query)) return false;
        return true;
    });
    const selected = coins.find(row => row.asset === active)
        || chart.slices.find(row => row.asset === active)
        || null;
    const centerValue = selected?.fiatEstimate ?? chart.total;
    const centerLabel = selected?.asset || 'Total USDC';

    return (
        <>
            <Menu />
            <main className="content pw-page">
                <div className="pw-container">
                    <section className="pw-hero">
                        <div className="pw-hero-copy">
                            <span className="pw-eyebrow">Binance Spot</span>
                            <h1>Carteira da plataforma</h1>
                            <p>
                                Composição real da conta Spot. O app do admin usa só o USDC alocado, lucros e perdas.
                            </p>
                        </div>
                        <div className="pw-hero-actions">
                            <button type="button" className="pw-btn" onClick={loadWallet} disabled={loading}>
                                {loading ? 'Atualizando...' : 'Atualizar mercados'}
                            </button>
                            {data?.sponsor?.id
                                ? <Link className="pw-ghost" to={`/users/${data.sponsor.id}`}>Ver usuário</Link>
                                : null}
                        </div>
                    </section>

                    {loading && !data
                        ? <div className="pw-empty">Carregando saldo da Binance...</div>
                        : null}

                    {data && (
                        <>
                            <section className="pw-stats">
                                <div className="pw-stat">
                                    <small>Total Spot</small>
                                    <strong>{formatUsd(data.walletTotal)} USDC</strong>
                                    <span>
                                        {Number(data.walletTotalBrl) > 0
                                            ? `≈ ${formatUsd(data.walletTotalBrl)} BRL`
                                            : 'Estimativa em USDC'}
                                    </span>
                                </div>
                                <div className="pw-stat">
                                    <small>Moedas</small>
                                    <strong>{coins.length}</strong>
                                    <span>Ativos com saldo na conta</span>
                                </div>
                                <div className="pw-stat">
                                    <small>Em ordem</small>
                                    <strong>{lockedCount}</strong>
                                    <span>Moedas com saldo travado</span>
                                </div>
                                <div className="pw-stat">
                                    <small>Conta Binance</small>
                                    <strong>{data.sponsor?.name || '—'}</strong>
                                    <span>{data.sponsor?.email || ''}</span>
                                </div>
                            </section>

                            <section className="pw-layout">
                                <div className="pw-panel">
                                    <div className="pw-panel-head">
                                        <div>
                                            <h2>Composição</h2>
                                            <p className="pw-muted">Fatias pelo valor estimado em USDC.</p>
                                        </div>
                                    </div>
                                    {chart.slices.length
                                        ? (
                                            <>
                                                <div className="pw-donut-wrap" aria-hidden="true">
                                                    <svg viewBox="0 0 320 320">
                                                        {chart.slices.map(slice => (
                                                            <path
                                                                key={slice.asset}
                                                                d={donutPath(160, 160, 132, 82, slice.start, slice.end)}
                                                                fill={slice.color}
                                                                className={active && active !== slice.asset ? 'is-dimmed' : ''}
                                                                onMouseEnter={() => setActive(slice.asset)}
                                                                onMouseLeave={() => setActive('')}
                                                                onClick={() => setActive(slice.asset)}
                                                            />
                                                        ))}
                                                    </svg>
                                                    <div className="pw-donut-center">
                                                        <strong>{formatUsd(centerValue)}</strong>
                                                        <small>{centerLabel}</small>
                                                    </div>
                                                </div>
                                                <div className="pw-selected">
                                                    <strong>{selected ? selected.asset : 'Passe o mouse numa fatia ou card'}</strong>
                                                    {selected
                                                        ? (
                                                            <div className="pw-selected-grid">
                                                                <div>
                                                                    <small>Participação</small>
                                                                    <span>{formatShare(selected.share || (chart.total ? selected.fiatEstimate / chart.total : 0))}</span>
                                                                </div>
                                                                <div>
                                                                    <small>Estimado</small>
                                                                    <span>{formatUsd(selected.fiatEstimate)} USDC</span>
                                                                </div>
                                                                {selected.available != null && (
                                                                    <div>
                                                                        <small>Livre</small>
                                                                        <span>{formatQty(selected.available)}</span>
                                                                    </div>
                                                                )}
                                                                {selected.onOrder != null && (
                                                                    <div>
                                                                        <small>Em ordem</small>
                                                                        <span>{formatQty(selected.onOrder)}</span>
                                                                    </div>
                                                                )}
                                                                {selected.otherCount
                                                                    ? (
                                                                        <div>
                                                                            <small>Agrupadas</small>
                                                                            <span>{selected.otherCount} moedas menores</span>
                                                                        </div>
                                                                    )
                                                                    : null}
                                                            </div>
                                                        )
                                                        : <p className="pw-muted" style={{ marginTop: 8 }}>O centro mostra o total até você escolher uma moeda.</p>}
                                                </div>
                                            </>
                                        )
                                        : <div className="pw-empty">Nenhuma moeda com valor estimado.</div>}
                                </div>

                                <div className="pw-panel">
                                    <div className="pw-panel-head">
                                        <div>
                                            <h2>Moedas na Binance</h2>
                                            <p className="pw-muted">Cards no estilo dos mercados, com livre, ordem e peso na carteira.</p>
                                        </div>
                                    </div>
                                    <div className="pw-toolbar">
                                        <input
                                            value={search}
                                            placeholder="Buscar BTC, USDC, HBAR..."
                                            onChange={event => setSearch(event.target.value)}
                                        />
                                        <select value={filter} onChange={event => setFilter(event.target.value)}>
                                            <option value="all">Todas</option>
                                            <option value="locked">Em ordem</option>
                                        </select>
                                        <button type="button" className="pw-ghost" onClick={() => { setSearch(''); setFilter('all'); }}>
                                            Limpar
                                        </button>
                                    </div>
                                    {visible.length
                                        ? (
                                            <div className="pw-coins">
                                                {visible.map(row => {
                                                    const share = chart.total > 0 ? row.fiatEstimate / chart.total : 0;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={row.asset}
                                                            className={`pw-coin${active === row.asset ? ' is-active' : ''}`}
                                                            onMouseEnter={() => setActive(row.asset)}
                                                            onMouseLeave={() => setActive('')}
                                                            onClick={() => setActive(row.asset)}
                                                        >
                                                            <div className="pw-coin-head">
                                                                <div className="pw-coin-id">
                                                                    <div className="pw-coin-icon" style={{ color: row.color }}>{row.asset.slice(0, 2)}</div>
                                                                    <div>
                                                                        <strong>{row.asset}</strong>
                                                                        <small>{formatShare(share)} da carteira</small>
                                                                    </div>
                                                                </div>
                                                                {row.onOrder > 0 ? <span className="pw-pill">Em ordem</span> : null}
                                                            </div>
                                                            <div className="pw-bar">
                                                                <span style={{ width: `${Math.max(share * 100, share > 0 ? 2 : 0)}%`, background: row.color }} />
                                                            </div>
                                                            <div className="pw-coin-metrics">
                                                                <div>
                                                                    <small>Livre</small>
                                                                    <span>{formatQty(row.available)}</span>
                                                                </div>
                                                                <div>
                                                                    <small>Em ordem</small>
                                                                    <span>{formatQty(row.onOrder)}</span>
                                                                </div>
                                                                <div>
                                                                    <small>Estimado</small>
                                                                    <span>{formatUsd(row.fiatEstimate)} USDC</span>
                                                                </div>
                                                                <div>
                                                                    <small>Peso</small>
                                                                    <span>{formatShare(share)}</span>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )
                                        : <div className="pw-empty">Nenhuma moeda encontrada.</div>}
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default PlatformWallet;
